// Add-to-calendar helpers for weekly recurring timetable classes.

const DAY_INDEX = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
const BYDAY = { Monday: "MO", Tuesday: "TU", Wednesday: "WE", Thursday: "TH", Friday: "FR", Saturday: "SA", Sunday: "SU" };

// Next date (from today) that falls on the class's weekday.
const nextOccurrence = (dayName) => {
  const target = DAY_INDEX[dayName] ?? 1;
  const d = new Date();
  const ahead = (target - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + ahead);
  return d;
};

const pad = (n) => String(n).padStart(2, "0");

// "YYYYMMDDTHHMMSS" local floating time from a date + "HH:MM"
const stamp = (date, time) => {
  const [h, m] = (time || "16:00").split(":").map(Number);
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(h)}${pad(m)}00`;
};

const classDescription = (cls) => {
  const bits = [];
  if (cls.style) bits.push(cls.style);
  if (cls.age_group) bits.push(`Ages ${cls.age_group}`);
  if (cls.level) bits.push(cls.level);
  bits.push("Kerry's — Dance & Performing Arts School, Whitstable");
  bits.push("www.kerrys.dance");
  return bits.join(" · ");
};

// Google Calendar "add event" link with weekly recurrence, pinned to UK time.
export const googleCalendarUrl = (cls) => {
  const date = nextOccurrence(cls.day);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `${cls.name} — Kerry's`,
    dates: `${stamp(date, cls.start_time)}/${stamp(date, cls.end_time)}`,
    ctz: "Europe/London",
    location: cls.venue || "Whitstable",
    details: classDescription(cls),
    recur: `RRULE:FREQ=WEEKLY;BYDAY=${BYDAY[cls.day] || "MO"}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Minimal Europe/London VTIMEZONE so Apple/Outlook honour UK clock changes.
const VTIMEZONE = [
  "BEGIN:VTIMEZONE",
  "TZID:Europe/London",
  "BEGIN:DAYLIGHT",
  "TZOFFSETFROM:+0000",
  "TZOFFSETTO:+0100",
  "TZNAME:BST",
  "DTSTART:19700329T010000",
  "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU",
  "END:DAYLIGHT",
  "BEGIN:STANDARD",
  "TZOFFSETFROM:+0100",
  "TZOFFSETTO:+0000",
  "TZNAME:GMT",
  "DTSTART:19701025T020000",
  "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU",
  "END:STANDARD",
  "END:VTIMEZONE",
].join("\r\n");

const icsEscape = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");

// Build and download a .ics file (opens straight into Apple Calendar / Outlook).
export const downloadIcs = (cls) => {
  const date = nextOccurrence(cls.day);
  const now = new Date();
  const dtstamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Kerrys Dance//Timetable//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    VTIMEZONE,
    "BEGIN:VEVENT",
    `UID:${cls.id || Math.random().toString(36).slice(2)}@kerrys.dance`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;TZID=Europe/London:${stamp(date, cls.start_time)}`,
    `DTEND;TZID=Europe/London:${stamp(date, cls.end_time)}`,
    `RRULE:FREQ=WEEKLY;BYDAY=${BYDAY[cls.day] || "MO"}`,
    `SUMMARY:${icsEscape(`${cls.name} — Kerry's`)}`,
    `LOCATION:${icsEscape(cls.venue || "Whitstable")}`,
    `DESCRIPTION:${icsEscape(classDescription(cls))}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(cls.name || "class").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-kerrys.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
